import { DownloadSimple, WarningCircle } from "phosphor-react";
import { useForm } from "react-hook-form";
import useFileDropper from "../../hooks/useFileDropper";
import useFileType from "../../hooks/useFileType";
import Button from "../Button";
import FormFileUploader from "../FormFileUploader";
import { useGetCompanySummary } from "../../api/admin/company";
import { useCompanyDepartmentSelector } from "../ReusableComponents/CompanyDepartmentSelector";
import HeadlessGenericDropdown, {
    DropdownItem,
} from "../ReusableComponents/HeadlessGenericDropdown";
import { ApiCompanySummary } from "../../models/api/admin/company";
import BulkUploadFailureReasons from "./BulkUploadFailureReasons";

type FormBulkUploadValues = {
    company: {
        id: string;
        item: string;
    };
    file: File[];
};

type FormBulkUploadProps = {
    onSubmit: (formData: FormData, companyId: string) => Promise<void>;
    isLoading: boolean;
    maxFileSizeMb?: number;
    disabled?: boolean;
    onClose: () => void;
    isBulkDelete?: boolean;
    failureReasons: string[];
    failureRef: React.MutableRefObject<HTMLDivElement | null>;
};

const FormBulkUpload: React.FC<FormBulkUploadProps> = ({
    onSubmit,
    isLoading,
    maxFileSizeMb = 30,
    disabled = false,
    onClose,
    isBulkDelete,
    failureReasons,
    failureRef,
}) => {
    const { selectedCompany } = useCompanyDepartmentSelector();

    const { companySummary, companySummaryError, isLoadingCompanySummary } =
        useGetCompanySummary();

    const companyDropdownItems = companySummary?.items.map((company) => ({
        item: company.companyName,
        id: company.companyId,
    }));

    const {
        handleSubmit,
        resetField,
        register,
        setValue,
        watch,
    } = useForm<FormBulkUploadValues>({
        mode: "onSubmit",
        defaultValues: {
            company: {
                id: selectedCompany?.companyId,
                item: selectedCompany?.companyName,
            },
            file: undefined,
        },
    });

    const file = watch("file");
    const company = watch("company");
    const selectedFile = file?.[0];

    const isDisabled = disabled || !company.id;
    const hasFile = !!selectedFile;

    const handleCompanySelect = (item: DropdownItem | null) => {
        const company: ApiCompanySummary | undefined = companySummary?.items.find(
            (company) => company.companyId === item?.id
        );

        if (company) {
            const { companyId: id, companyName: item } = company;
            setValue("company", { id, item });
        }
    };

    const { loadFile, fileError } = useFileType({
        fileType: ["csv"],
        maxFileSizeMb: maxFileSizeMb,
    });

    const handleSetValue = (file: File) => {
        setValue("file", [file]);
    };

    const {
        onChange: registerFileOnChange,
        ...registerFileProps
    } = register("file");

    const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (
        event
    ) => {
        registerFileOnChange(event);

        const selected = event.target.files?.[0];
        if (!selected) return;

        setValue("file", [selected], { shouldDirty: true });
        loadFile(selected);
    };

    const { handleDrag, handleDrop, dragActive } = useFileDropper({
        handleSetValue,
        onLoadFile: loadFile,
    });

    const handleImport = (data: FormBulkUploadValues) => {
        if (fileError) return;
        if (file === undefined) return;

        const formData = new FormData();
        const companyId = data.company.id;

        if (data.file) {
            formData.append("importFile", data.file[0]);
        }
        onSubmit(formData, companyId);
    };

    const handleReset = () => {
        resetField("file");
    };

    return (
        <form onSubmit={handleSubmit(handleImport)}>
            <div className="sticky mx-4 border border-t-0 border-S2D-neutral-80" />
            <div className=" flex max-h-96 flex-col gap-4 overflow-y-auto p-4">
                <div className="my-2 text-sm font-bold ">
                    <a
                        href={
                            isBulkDelete
                                ? require("../../DriversDelete.csv")
                                : require("../../DriversCreate.csv")
                        }
                        download={
                            isBulkDelete
                                ? "DriversDelete.csv"
                                : "DriversCreate.csv"
                        }
                        className="flex w-fit flex-row items-baseline gap-2 rounded-sm border-2 border-black py-2 px-4"
                    >
                        <DownloadSimple />
                        Download a sample spreadsheet
                    </a>
                </div>
                {companySummary ? (
                    <>
                        <HeadlessGenericDropdown
                            dropdownPlaceholder="All clients"
                            dropdownData={companyDropdownItems}
                            selectedItem={company}
                            handleItemChange={handleCompanySelect}
                            error={companySummaryError}
                            isLoading={isLoadingCompanySummary}
                            label={undefined}
                        />
                        <FormFileUploader
                            isDirty={hasFile}
                            error={fileError}
                            file={file}
                            maxFileSizeMb={maxFileSizeMb}
                            onDrag={handleDrag}
                            onDrop={handleDrop}
                            dragActive={dragActive}
                            onReset={handleReset}
                            label="Drop a CSV file here to upload or"
                            {...registerFileProps}
                            onChange={handleFileChange}
                            disabled={isDisabled}
                        />
                    </>
                ) : null}
                <div className="flex gap-2 text-sm">
                    <WarningCircle size={20} weight="fill" />
                    {isBulkDelete
                        ? "To undo this action you will have to contact Safe2Drive"
                        : "Duplicate drivers will be removed"}
                </div>
                <BulkUploadFailureReasons
                    failureReasons={failureReasons}
                    failureRef={failureRef}
                />
            </div>
            <div className="flex justify-between rounded-b-sm border-t border-S2D-neutral-80 bg-S2D-neutral-90 p-2">
                <Button variant="tertiary" onClick={onClose}>
                    Cancel
                </Button>
                <Button type="submit" loading={isLoading} disabled={!hasFile}>
                    Start import
                </Button>
            </div>
        </form>
    );
};

export default FormBulkUpload;
